import React, { useState, useMemo, useEffect } from 'react';
import { useDocumentTypes, useCreateDocumentType, useUpdateDocumentType, useDeleteDocumentType } from '../../hooks/useAdmin';
import { debounce } from '../../utils/helpers';
import TableHeader from '../../components/common/TableHeader';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import './AdminDocuments.css';

const AdminDocuments = () => {
  const [filters, setFilters] = useState({
    required_for: 'all', // 'all', 'employee', 'employer'
    is_active: true, // true = active (default), undefined = all, false = inactive
    is_common: false, // toggle for common documents
    search: ''
  });
  
  const [searchInput, setSearchInput] = useState('');
  
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc'
  });
  
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  
  // Build backend filters
  const backendFilters = useMemo(() => {
    const backendFilter = {};
    
    // Handle required_for filter
    if (filters.required_for === 'employee') {
      backendFilter.is_required_for_employee = true;
    } else if (filters.required_for === 'employer') {
      backendFilter.is_required_for_employer = true;
    }
    // 'all' means no filter on required_for
    
    // Handle active status filter
    if (filters.is_active === true) {
      backendFilter.is_active = true;
    } else if (filters.is_active === false) {
      backendFilter.is_active = false;
    }
    // undefined means 'all documents' - no filter
    
    // Handle common documents toggle
    if (filters.is_common) {
      backendFilter.is_common = true;
    }
    // false means no filter
    
    if (filters.search) {
      backendFilter.search = filters.search;
    }
    return backendFilter;
  }, [filters]);
  
  const { data: documents, isLoading } = useDocumentTypes(backendFilters);

  // Debounced search handler - use useMemo to persist the debounced function
  const debouncedSearch = useMemo(
    () => debounce((searchValue) => {
      setFilters(prev => ({ ...prev, search: searchValue }));
    }, 300),
    []
  );

  // Update search input and trigger debounced search
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    debouncedSearch(value);
  };

  // Sync searchInput with filters.search on mount or when filters.search changes externally
  useEffect(() => {
    if (filters.search !== searchInput) {
      setSearchInput(filters.search);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search]);
  const createMutation = useCreateDocumentType();
  const updateMutation = useUpdateDocumentType();
  const deleteMutation = useDeleteDocumentType();

  const handleSort = (key, direction) => {
    setSortConfig({ key, direction });
  };

  // Filter documents client-side for search
  const filteredDocuments = useMemo(() => {
    if (!documents) return [];
    if (!filters.search) return documents;
    
    const searchLower = filters.search.toLowerCase();
    return documents.filter(doc => 
      doc.name?.toLowerCase().includes(searchLower) ||
      doc.document_key?.toLowerCase().includes(searchLower)
    );
  }, [documents, filters.search]);

  const sortedDocuments = useMemo(() => {
    if (!filteredDocuments || !sortConfig.key) return filteredDocuments;
    
    return [...filteredDocuments].sort((a, b) => {
      let aVal, bVal;
      
      switch (sortConfig.key) {
        case 'name':
          aVal = a.name?.toLowerCase() || '';
          bVal = b.name?.toLowerCase() || '';
          break;
        case 'document_key':
          aVal = a.document_key?.toLowerCase() || '';
          bVal = b.document_key?.toLowerCase() || '';
          break;
        case 'is_active':
          aVal = a.is_active ? 1 : 0;
          bVal = b.is_active ? 1 : 0;
          break;
        case 'is_common':
          aVal = a.is_common ? 1 : 0;
          bVal = b.is_common ? 1 : 0;
          break;
        default:
          return 0;
      }
      
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredDocuments, sortConfig]);
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [formData, setFormData] = useState({
    document_key: '',
    name: '',
    is_required_for_employee: false,
    is_required_for_employer: false,
    is_common: false,
    is_active: true
  });

  const handleOpenModal = (doc = null) => {
    if (doc) {
      setEditingDoc(doc);
      setFormData({
        document_key: doc.document_key || '',
        name: doc.name || '',
        is_required_for_employee: doc.is_required_for_employee || false,
        is_required_for_employer: doc.is_required_for_employer || false,
        is_common: doc.is_common || false,
        is_active: doc.is_active !== undefined ? doc.is_active : true
      });
    } else {
      setEditingDoc(null);
      setFormData({
        document_key: '',
        name: '',
        is_required_for_employee: false,
        is_required_for_employer: false,
        is_common: false,
        is_active: true
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingDoc(null);
    setFormData({
      document_key: '',
      name: '',
      is_required_for_employee: false,
      is_required_for_employer: false,
      is_common: false,
      is_active: true
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = {
      document_key: formData.document_key,
      name: formData.name,
      is_required_for_employee: formData.is_required_for_employee,
      is_required_for_employer: formData.is_required_for_employer,
      is_common: formData.is_common,
      is_active: formData.is_active
    };
    
    if (editingDoc) {
      updateMutation.mutate({ id: editingDoc.id, data }, { onSuccess: handleCloseModal });
    } else {
      createMutation.mutate(data, { onSuccess: handleCloseModal });
    }
  };

  const handleDelete = (id) => {
    setShowDeleteConfirm(id);
  };

  const handleConfirmDelete = () => {
    if (showDeleteConfirm) {
      deleteMutation.mutate(showDeleteConfirm);
      setShowDeleteConfirm(null);
    }
  };

  // Check if any filter is applied (not at default state)
  const hasActiveFilters = useMemo(() => {
    return (
      filters.required_for !== 'all' ||
      filters.is_active !== true ||
      filters.is_common !== false ||
      filters.search !== ''
    );
  }, [filters]);

  // Reset all filters to default
  const handleResetFilters = () => {
    setFilters({
      required_for: 'all',
      is_active: true,
      is_common: false,
      search: ''
    });
    setSearchInput('');
  };

  if (isLoading) {
    return (
      <div className="admin-documents__loading">
        <div className="admin-documents__spinner"></div>
        <p>Loading document types...</p>
      </div>
    );
  }

  return (
    <div className="admin-documents">
      <div className="admin-documents__header">
        <h1 className="admin-documents__title">Document Types Management</h1>
        <div className="admin-documents__header-actions">
          <div className="admin-documents__filters">
            <select
              value={filters.required_for}
              onChange={(e) => {
                setFilters(prev => ({
                  ...prev,
                  required_for: e.target.value
                }));
              }}
              className="admin-documents__filter"
            >
              <option value="all">All Documents</option>
              <option value="employee">Required for Employee</option>
              <option value="employer">Required for Employer</option>
            </select>
            <select
              value={filters.is_active === undefined ? 'all' : filters.is_active ? 'active' : 'inactive'}
              onChange={(e) => {
                const value = e.target.value;
                setFilters(prev => ({
                  ...prev,
                  is_active: value === 'all' ? undefined : value === 'active'
                }));
              }}
              className="admin-documents__filter"
            >
              <option value="active">Active Documents</option>
              <option value="all">All Documents</option>
              <option value="inactive">Inactive Documents</option>
            </select>
            <div className="admin-documents__toggle-wrapper">
              <label className="admin-documents__toggle-container">
                <input
                  type="checkbox"
                  checked={filters.is_common}
                  onChange={(e) => {
                    setFilters(prev => ({
                      ...prev,
                      is_common: e.target.checked
                    }));
                  }}
                  className="admin-documents__toggle-input"
                />
                <span className="admin-documents__toggle-slider"></span>
                <span className="admin-documents__toggle-label">Common Document</span>
              </label>
              <span 
                className="admin-documents__toggle-hint" 
                data-tooltip="Common documents are required in all job roles for employees or employers"
              >
                ℹ️
              </span>
            </div>
            <input
              type="text"
              placeholder="Search documents..."
              value={searchInput}
              onChange={handleSearchChange}
              className="admin-documents__search"
            />
            {hasActiveFilters && (
              <button
                onClick={handleResetFilters}
                className="admin-documents__reset-btn"
                title="Reset all filters"
              >
                Reset Filters
              </button>
            )}
          </div>
          <button
            onClick={() => handleOpenModal()}
            className="admin-documents__add-btn"
          >
            + Add Document Type
          </button>
        </div>
      </div>

      <div className="admin-documents__table-container">
        <table className="admin-documents__table">
          <thead>
            <tr>
              <TableHeader 
                sortKey="document_key" 
                currentSortKey={sortConfig.key} 
                sortDirection={sortConfig.direction}
                onSort={handleSort}
              >
                Document Key
              </TableHeader>
              <TableHeader 
                sortKey="name" 
                currentSortKey={sortConfig.key} 
                sortDirection={sortConfig.direction}
                onSort={handleSort}
              >
                Name
              </TableHeader>
              <th>Required For</th>
              <TableHeader 
                sortKey="is_common" 
                currentSortKey={sortConfig.key} 
                sortDirection={sortConfig.direction}
                onSort={handleSort}
              >
                Type
              </TableHeader>
              <TableHeader 
                sortKey="is_active" 
                currentSortKey={sortConfig.key} 
                sortDirection={sortConfig.direction}
                onSort={handleSort}
              >
                Status
              </TableHeader>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedDocuments && sortedDocuments.length > 0 ? (
              sortedDocuments.map((doc) => (
                <tr key={doc.id}>
                  <td>
                    <code className="admin-documents__key">{doc.document_key}</code>
                  </td>
                  <td>{doc.name}</td>
                  <td>
                    <div className="admin-documents__tags">
                      {doc.is_required_for_employee && (
                        <span className="admin-documents__tag admin-documents__tag--employee">
                          Employee
                        </span>
                      )}
                      {doc.is_required_for_employer && (
                        <span className="admin-documents__tag admin-documents__tag--employer">
                          Employer
                        </span>
                      )}
                      {!doc.is_required_for_employee && !doc.is_required_for_employer && (
                        <span className="admin-documents__tag admin-documents__tag--none">
                          None
                        </span>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className={`admin-documents__badge ${doc.is_common ? 'admin-documents__badge--common' : 'admin-documents__badge--specific'}`}>
                      {doc.is_common ? 'Common' : 'Job Role Specific'}
                    </span>
                  </td>
                  <td>
                    <span className={`admin-documents__badge ${doc.is_active ? 'admin-documents__badge--active' : 'admin-documents__badge--inactive'}`}>
                      {doc.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div className="admin-documents__actions">
                      <button
                        onClick={() => handleOpenModal(doc)}
                        className="admin-documents__btn admin-documents__btn--edit"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(doc.id)}
                        className="admin-documents__btn admin-documents__btn--delete"
                        disabled={deleteMutation.isPending}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="admin-documents__empty">No document types found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {isModalOpen && (
        <div className="admin-documents__modal-overlay" onClick={handleCloseModal}>
          <div className="admin-documents__modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-documents__modal-header">
              <h2>{editingDoc ? 'Edit Document Type' : 'Add Document Type'}</h2>
              <button onClick={handleCloseModal} className="admin-documents__modal-close">×</button>
            </div>
            <form onSubmit={handleSubmit} className="admin-documents__form">
              <div className="admin-documents__field">
                <label>Document Key <span className="admin-documents__required">*</span></label>
                <input
                  type="text"
                  value={formData.document_key}
                  onChange={(e) => setFormData({ ...formData, document_key: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                  placeholder="e.g., gdc_registration_certificate"
                  required
                  disabled={!!editingDoc}
                  pattern="[a-z0-9_]+"
                  title="Only lowercase letters, numbers, and underscores allowed"
                />
                {editingDoc && (
                  <small className="admin-documents__hint">Document key cannot be changed after creation</small>
                )}
              </div>
              <div className="admin-documents__field">
                <label>Name <span className="admin-documents__required">*</span></label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., GDC Registration Certificate"
                  required
                />
              </div>
              <div className="admin-documents__field">
                <label>Required For</label>
                <div className="admin-documents__checkboxes">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.is_required_for_employee}
                      onChange={(e) => setFormData({ ...formData, is_required_for_employee: e.target.checked })}
                    />
                    Required for Employees
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.is_required_for_employer}
                      onChange={(e) => setFormData({ ...formData, is_required_for_employer: e.target.checked })}
                    />
                    Required for Employers
                  </label>
                </div>
              </div>
              <div className="admin-documents__field">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.is_common}
                    onChange={(e) => setFormData({ ...formData, is_common: e.target.checked })}
                  />
                  Common Document
                </label>
                <small className="admin-documents__hint">
                  Common documents are required in all job roles for employees or employers. If unchecked, this document will only be required when mapped to specific job roles.
                </small>
              </div>
              <div className="admin-documents__field">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                  Active
                </label>
                <small className="admin-documents__hint">
                  Inactive documents won't appear in job role mappings
                </small>
              </div>
              <div className="admin-documents__actions">
                <button type="button" onClick={handleCloseModal} className="admin-documents__btn admin-documents__btn--cancel">
                  Cancel
                </button>
                <button type="submit" className="admin-documents__btn admin-documents__btn--save">
                  {editingDoc ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        isOpen={!!showDeleteConfirm}
        title="Delete Document Type"
        message="Are you sure you want to delete this document type? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        onConfirm={handleConfirmDelete}
        onCancel={() => setShowDeleteConfirm(null)}
      />
    </div>
  );
};

export default AdminDocuments;

